import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Package, Clock, CheckCircle, Truck, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUSES = [
  { value: "pending", label: "Pending", color: "text-yellow-500 bg-yellow-500/10" },
  { value: "paid", label: "Paid", color: "text-emerald-500 bg-emerald-500/10" },
  { value: "processing", label: "Processing", color: "text-sky-500 bg-sky-500/10" },
  { value: "shipped", label: "Shipped", color: "text-purple-500 bg-purple-500/10" },
  { value: "delivered", label: "Delivered", color: "text-emerald-500 bg-emerald-500/10" },
  { value: "cancelled", label: "Cancelled", color: "text-red-500 bg-red-500/10" },
];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${newStatus}`);
      setOrders(orders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
      toast.success("Order status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusColor = (status) => {
    const s = STATUSES.find(s => s.value === status);
    return s ? s.color : "text-slate-500 bg-slate-500/10";
  };

  const filteredOrders = filterStatus === "all" 
    ? orders 
    : orders.filter(o => o.status === filterStatus);

  const totalRevenue = orders
    .filter(o => ["paid", "processing", "shipped", "delivered"].includes(o.status))
    .reduce((sum, o) => sum + o.total, 0);

  return (
    <AdminLayout title="Manage Orders">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card-surface p-4">
          <p className="text-slate-400 text-sm">Total Orders</p>
          <p className="text-2xl font-bold text-white">{orders.length}</p>
        </div>
        <div className="card-surface p-4">
          <p className="text-slate-400 text-sm">Pending</p>
          <p className="text-2xl font-bold text-yellow-500">{orders.filter(o => o.status === "pending").length}</p>
        </div>
        <div className="card-surface p-4">
          <p className="text-slate-400 text-sm">Processing</p>
          <p className="text-2xl font-bold text-sky-500">{orders.filter(o => o.status === "processing").length}</p>
        </div>
        <div className="card-surface p-4">
          <p className="text-slate-400 text-sm">Revenue</p>
          <p className="text-2xl font-bold text-emerald-500">R{totalRevenue.toFixed(2)}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-400">{filteredOrders.length} orders</p>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="input-dark w-40" data-testid="filter-status">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-[#111827] border-white/10">
            <SelectItem value="all">All Orders</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No orders found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <div key={order.id} className="card-surface p-6" data-testid={`order-${order.id}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className={`text-xs font-mono uppercase px-2 py-1 ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                    <span className="text-slate-500 text-sm font-mono">#{order.id.slice(0, 8)}</span>
                  </div>
                  <p className="text-white">{order.user_name} ({order.user_email})</p>
                  <p className="text-slate-400 text-sm">{new Date(order.created_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-lg font-bold text-white">R{order.total.toFixed(2)}</p>
                    <p className="text-slate-400 text-sm">{order.items.length} items</p>
                  </div>
                  <Select 
                    value={order.status} 
                    onValueChange={(value) => updateStatus(order.id, value)}
                  >
                    <SelectTrigger className="input-dark w-36" data-testid={`status-select-${order.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-white/10">
                      {STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Order Items */}
              <div className="space-y-2 pt-4 border-t border-white/10">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 text-sm">
                    <img 
                      src={item.product_image || "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=100"}
                      alt={item.product_name}
                      className="w-10 h-10 object-cover"
                    />
                    <div className="flex-1">
                      <span className="text-white">{item.product_name}</span>
                      {item.size && <span className="text-slate-400 ml-2">(Size: {item.size})</span>}
                    </div>
                    <span className="text-slate-400">x{item.quantity}</span>
                    <span className="text-white">R{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Shipping */}
              {order.shipping_address && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Ship to</p>
                  <p className="text-slate-300 text-sm">
                    {order.shipping_address.name} • {order.shipping_address.phone}<br/>
                    {order.shipping_address.address}, {order.shipping_address.city}, {order.shipping_address.postalCode}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrders;
